/**
 * Onglet « File de rapprochement ».
 *
 * Les paiements que l'automatique n'a pas placés, **du plus ancien au plus
 * récent** par défaut : on traite dans l'ordre d'arrivée, ce qui évite qu'un
 * petit paiement de juin reste au fond de la file pour toujours. Le tri par
 * montant décroissant — placer l'argent le plus vite — reste à un clic.
 *
 * Les deux tris se font **en base**. Trier cent lignes prélevées sur 8 715 ne
 * trie rien : cela réordonne un échantillon.
 *
 * Chaque ligne se déplie et charge alors ses suggestions — jamais avant. Une
 * file de cent paiements ne déclenche donc pas cinq cents requêtes au
 * chargement.
 *
 * ## Ce que le premier essai réel a corrigé
 *
 * Trois manques, tous constatés sur le premier lot importé :
 *
 * 1. **Aucune issue en dehors de « hors périmètre ».** Les suggestions
 *    échouaient silencieusement, et un paiement légitime dont la référence
 *    n'avait pas été comprise n'avait donc aucun geste correct disponible. D'où
 *    la recherche manuelle, toujours accessible, jamais dépendante des
 *    suggestions.
 * 2. **Aucune raison affichée.** La file ne disait pas pourquoi une ligne y
 *    était, alors que les raisons appellent des gestes très différents.
 * 3. **Vingt-cinq clics identiques.** La quasi-totalité du premier lot était
 *    des communications d'un autre système, à classer une par une. D'où la
 *    sélection multiple.
 *
 * ## Ce que l'écran ne fait pas
 *
 * Il n'applique rien tout seul, même sur une suggestion très forte. Ces
 * paiements sont précisément ceux dont la communication ne prouve rien : le
 * dernier mot revient à celui qui connaît les familles.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle, ArrowDownUp, BadgeCheck, ChevronDown, ChevronRight, CircleSlash,
  Landmark, Link2, Loader2, RefreshCw, Search, Sparkles, Undo2, Wallet, X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  allocatePayment, countPendingPayments, linkIbanToFamily, loadAllocations,
  loadPaymentText, loadPendingPayments, markManyOutOfScope, markOutOfScope, undoAllocation,
  type PendingPayment, type TextePaiement,
} from '../../../lib/payments/actions.supabase';
import {
  LIBELLES_DE_TRI, TRI_PAR_DEFAUT, lireRecherche, phraseDuJournal, type Tri,
} from '../../../lib/payments/journal';
import { suggestGatewaySupabase } from '../../../lib/payments/gateway.suggest.supabase';
import { isConfident, suggestFamilies, type Suggestion } from '../../../lib/payments/suggest';
import { searchManually } from '../../../lib/payments/search.supabase';
import { classifyOutOfScope } from '../../../lib/payments/outOfScope';
import { queueReason } from '../../../lib/payments/queueReason';
import { messageErreur } from '../../../lib/payments/erreur';
import {
  construireRepartition, phraseDeRepartition, placeEntierement,
  type DestinationDuReste,
} from '../../../lib/payments/repartition';
import CommunicationComplete from './CommunicationComplete';
import CompteDeLaFamille from './CompteDeLaFamille';
import FacturesCitees from './FacturesCitees';
import LienFacture from './LienFacture';
import SaisieMontant from './SaisieMontant';
import VirementLuDeuxFois from './VirementLuDeuxFois';
import { dateCourte, euros } from './money';

export default function ReconciliationQueue({
  rafraichir, onCount, rechercheInitiale,
}: {
  rafraichir: number;
  /**
   * Une recherche imposée par la page, quand l'écran « Rapprochés » renvoie
   * ici : « 3 mouvements de cette recherche attendent encore dans la file ».
   * Le clic doit arriver avec la recherche déjà faite, sinon il dépose
   * l'administration devant 3 028 lignes et la même question.
   */
  rechercheInitiale?: string;
  /**
   * Le compte reel de la file, remonte a la page apres chaque rechargement.
   *
   * La page comptait de son cote, et seulement apres un import : le badge de
   * l'onglet restait donc a 1 alors que la file etait vide. La file est la
   * seule a savoir ce qu'elle contient — c'est elle qui doit le dire.
   */
  onCount?: (nombre: number) => void;
}) {
  const [file, setFile] = useState<PendingPayment[]>([]);
  const [chargement, setChargement] = useState(true);
  const [ouvert, setOuvert] = useState<string | null>(null);
  const [selection, setSelection] = useState<Set<string>>(new Set());
  const [lot, setLot] = useState(false);
  const [tri, setTri] = useState<Tri>(TRI_PAR_DEFAUT);
  /*
    Deux états pour la recherche : ce qui est TAPÉ, et ce qui est CHERCHÉ. Sans
    cette séparation, chaque frappe déclenche une requête. L'envoi se fait au
    `submit` — une recherche est un geste, pas un effet de bord de la frappe.
    C'est la même règle que dans le journal, et volontairement la même.
  */
  const [saisie, setSaisie] = useState(rechercheInitiale ?? '');
  const [recherche, setRecherche] = useState(rechercheInitiale ?? '');
  /*
    Le total RÉEL, demandé à la base. La file n'en montre que les cent
    premières lignes : sans ce chiffre, l'écran annonçait « 100 paiement(s) à
    placer » sur 8 715 et personne ne pouvait le savoir en le lisant.
  */
  const [total, setTotal] = useState<number | null>(null);

  /*
    `onCount` passe par une reference, et ce n'est pas un detail de style.

    La page reagit a ce rappel en changeant son propre etat, donc elle se
    redessine, donc elle fournit une NOUVELLE fonction. Si `charger` en
    dependait, il serait recree, l'effet se redeclencherait, la file se
    rechargerait, la page se redessinerait — et ainsi de suite. C'est exactement
    ce qui s'est produit : « Chargement de la file... » sans fin, aucune ligne
    affichee, requetes en boucle.

    La reference garde toujours le dernier rappel sans entrer dans les
    dependances. Le composant ne peut donc plus boucler a cause de la maniere
    dont on l'appelle — la corriger cote page aussi serait juste, mais ne
    protegerait pas le prochain appelant.
  */
  const rappelCompte = useRef(onCount);
  useEffect(() => { rappelCompte.current = onCount; }, [onCount]);

  const charger = useCallback(async (ordre: Tri, quoi: string) => {
    setChargement(true);
    try {
      /*
        Les deux lectures partent ensemble et se règlent séparément : le
        comptage est un `count` sans données, la liste une page triée. Un
        comptage en échec ne doit pas priver l'écran de sa file.
      */
      const [lignes, compte] = await Promise.allSettled([
        loadPendingPayments(100, ordre, quoi),
        countPendingPayments(quoi),
      ]);

      if (lignes.status === 'rejected') throw lignes.reason;
      setFile(lignes.value);
      setSelection(new Set());

      const n = compte.status === 'fulfilled' ? compte.value.nombre : null;
      setTotal(n);
      /*
        Le badge de l'onglet compte LA FILE, pas le résultat d'une recherche.
        Le lui faire suivre ferait afficher « 3 » à côté de « File de
        rapprochement » parce que quelqu'un a cherché un nom — et ce badge est
        précisément ce sur quoi on se fie pour savoir s'il reste du travail.
      */
      if (!quoi) rappelCompte.current?.(n ?? lignes.value.length);
    } catch (e) {
      toast.error(messageErreur(e, 'Chargement de la file impossible'));
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => { void charger(tri, recherche); }, [charger, rafraichir, tri, recherche]);

  /* Ce que la règle a compris de la saisie, pour que l'écran puisse le dire. */
  const comprise = useMemo(() => lireRecherche(recherche), [recherche]);

  const raisons = useMemo(
    () => new Map(file.map((p) => [p.id, queueReason(p)])),
    [file],
  );
  /* La somme de la PAGE affichée. Le total de la file, lui, vient du `count`. */
  const centimesAffiches = file.reduce((s, p) => s + p.unallocatedCents, 0);
  const autresSystemes = file.filter((p) => raisons.get(p.id)?.probablyOutOfScope);
  const selectionnes = file.filter((p) => selection.has(p.id));
  const totalSelection = selectionnes.reduce((s, p) => s + p.unallocatedCents, 0);

  const basculerSelection = (id: string) => {
    const s = new Set(selection);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelection(s);
  };

  const classerLeLot = async () => {
    setLot(true);
    try {
      const n = await markManyOutOfScope(selectionnes.map((p) => ({
        paymentId: p.id,
        amountCents: p.unallocatedCents,
        note: raisons.get(p.id)?.label ?? 'Hors périmètre centres de vacances',
      })));
      toast.success(`${n} paiement(s) classé(s) hors périmètre`);
      await charger(tri, recherche);
    } catch (e) {
      toast.error(messageErreur(e, 'Classement refusé'));
    } finally {
      setLot(false);
    }
  };

  /*
    Aucun retour anticipé sur `chargement` ni sur une file vide : ils
    emportaient la barre de recherche avec eux. Une recherche qui ne trouve
    rien affichait alors « La file est vide » — un message faux, et sans plus
    aucun moyen d'effacer la recherche qui l'avait produit.
  */
  const rien = !chargement && file.length === 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          {/*
            La phrase DIT ce qu'elle ne montre pas. L'écran annonçait
            « 100 paiement(s) à placer » sur 8 715, sans un mot : une liste
            tronquée qui se taît fait conclure à tort.
          */}
          <p className="text-sm text-gray-700">
            {phraseDuJournal(file.length, total, centimesAffiches, euros)}
            {total !== null && total > file.length && (
              <span className="text-gray-500">
                {' '}Le montant porte sur les lignes affichées.
              </span>
            )}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/*
            Le tri, demandé le 6 septembre 2026. Un `select` et non deux
            boutons : les tris sont exclusifs, et la liste doit dire lequel est
            actif sans qu'on ait à comparer deux états visuels.
          */}
          <label className="flex items-center gap-1.5 text-sm text-gray-600">
            <ArrowDownUp className="h-4 w-4 text-gray-400" />
            <span className="sr-only sm:not-sr-only">Trier</span>
            <select
              value={tri}
              onChange={(e) => setTri(e.target.value as Tri)}
              className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-800"
            >
              {(Object.keys(LIBELLES_DE_TRI) as Tri[]).map((t) => (
                <option key={t} value={t}>{LIBELLES_DE_TRI[t]}</option>
              ))}
            </select>
          </label>

          <button
            onClick={() => void charger(tri, recherche)}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4" /> Rafraîchir
          </button>
        </div>
      </div>

      {/*
        La recherche, ajoutée le 19 septembre 2026. La file en comptait 3 028 et
        n'en montrait que cent : un virement précis y était hors d'atteinte, et
        on le cherchait donc dans « Rapprochés », où il ne pouvait pas être —
        cet onglet ne montre que ce qui a été décidé.

        Elle se fait EN BASE, comme celle du journal et avec la même règle de
        lecture : chercher dans les cent lignes reçues ne chercherait que dans
        un échantillon.
      */}
      <form
        className="flex flex-wrap gap-2"
        onSubmit={(e) => { e.preventDefault(); setRecherche(saisie.trim()); }}
      >
        <div className="relative min-w-0 flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          <input
            value={saisie}
            onChange={(e) => setSaisie(e.target.value)}
            placeholder="Montant, nom, compte, date, communication ou n° de facture"
            className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-8 text-sm"
          />
          {saisie && (
            <button
              type="button"
              onClick={() => { setSaisie(''); setRecherche(''); }}
              aria-label="Effacer la recherche"
              className="absolute right-2 top-2.5 text-gray-400 hover:text-gray-700"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <button
          type="submit"
          className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
        >
          <Search className="h-4 w-4" /> Chercher
        </button>
      </form>

      {/*
        Ce que la règle a compris. Une recherche qui interprète en silence est
        une recherche qu'on ne peut pas corriger.
      */}
      {comprise.explication && <p className="text-xs text-gray-500">{comprise.explication}</p>}

      {chargement && (
        <div className="flex items-center gap-2 py-8 text-sm text-gray-600">
          <Loader2 className="h-4 w-4 animate-spin" /> Chargement de la file…
        </div>
      )}

      {rien && recherche && (
        <p className="rounded-lg border border-gray-200 bg-white p-8 text-center text-sm text-gray-600">
          Aucun paiement en attente ne correspond à « {recherche} ».
          <span className="mt-1 block text-gray-500">
            S'il a déjà été rapproché, il est dans l'onglet « Rapprochés ».
          </span>
        </p>
      )}

      {rien && !recherche && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-8 text-center">
          <BadgeCheck className="mx-auto h-10 w-10 text-green-600" />
          <p className="mt-3 text-sm font-medium text-green-900">La file est vide.</p>
          <p className="mt-1 text-sm text-green-700">
            Tous les paiements enregistrés sont affectés. L'onglet « Rapprochés » montre
            ce qui a été écrit.
          </p>
        </div>
      )}

      {/* Le raccourci qui évite les vingt-cinq clics identiques. */}
      {autresSystemes.length > 1 && selection.size === 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm">
          <p className="text-amber-900">
            <span className="font-medium">{autresSystemes.length} paiement(s)</span> portent une
            communication qui appartient à un autre système (garderie, parascolaire, études
            dirigées) ou à une activité hors de cette comptabilité.
          </p>
          <button
            onClick={() => setSelection(new Set(autresSystemes.map((p) => p.id)))}
            className="shrink-0 rounded-md border border-amber-300 bg-white px-3 py-1.5 text-sm font-medium text-amber-900 hover:bg-amber-100"
          >
            Les sélectionner
          </button>
        </div>
      )}

      {selection.size > 0 && (
        <div className="sticky top-2 z-10 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-indigo-300 bg-indigo-50 p-3 shadow-sm">
          <p className="text-sm text-indigo-900">
            <span className="font-medium">{selection.size} sélectionné(s)</span>,
            <span className="ml-1 tabular-nums">{euros(totalSelection)}</span>
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setSelection(new Set())}
              disabled={lot}
              className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Tout désélectionner
            </button>
            <button
              onClick={() => void classerLeLot()}
              disabled={lot}
              className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {lot ? <Loader2 className="h-4 w-4 animate-spin" /> : <CircleSlash className="h-4 w-4" />}
              Classer hors périmètre
            </button>
          </div>
        </div>
      )}

      {file.length > 0 && (
      <div className="divide-y divide-gray-200 overflow-hidden rounded-lg border border-gray-200 bg-white">
        {file.map((p) => (
          <LignePaiement
            key={p.id}
            paiement={p}
            raison={raisons.get(p.id)!}
            selectionne={selection.has(p.id)}
            onSelection={() => basculerSelection(p.id)}
            ouvert={ouvert === p.id}
            basculer={() => setOuvert(ouvert === p.id ? null : p.id)}
            apresAction={() => { setOuvert(null); void charger(tri, recherche); }}
          />
        ))}
      </div>
      )}
    </div>
  );
}

function LignePaiement({
  paiement, raison, selectionne, onSelection, ouvert, basculer, apresAction,
}: {
  paiement: PendingPayment;
  raison: ReturnType<typeof queueReason>;
  selectionne: boolean;
  onSelection: () => void;
  ouvert: boolean;
  basculer: () => void;
  apresAction: () => void;
}) {
  return (
    <div className={clsx(ouvert && 'bg-gray-50', selectionne && 'bg-indigo-50/60')}>
      <div className="flex items-start gap-3 px-4 py-3">
        <input
          type="checkbox"
          checked={selectionne}
          onChange={onSelection}
          aria-label={`Sélectionner le paiement de ${euros(paiement.unallocatedCents)}`}
          className="mt-1 h-4 w-4 shrink-0 rounded border-gray-300 text-indigo-600"
        />
        <button onClick={basculer} className="flex min-w-0 flex-1 items-start gap-3 text-left">
          {ouvert
            ? <ChevronDown className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
            : <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />}

          <span className="w-28 shrink-0 text-right font-medium tabular-nums text-gray-900">
            {euros(paiement.unallocatedCents)}
          </span>
          <span className="w-20 shrink-0 text-xs text-gray-500">{dateCourte(paiement.valueDate)}</span>

          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm text-gray-900">
              {paiement.communicationRaw || <em className="text-gray-400">communication vide</em>}
            </span>
            <span className="mt-0.5 block truncate text-xs text-gray-500">
              {paiement.counterpartyName ?? 'émetteur inconnu'}
              {paiement.counterpartyIban && (
                <span className="ml-2 font-mono">{paiement.counterpartyIban}</span>
              )}
            </span>
          </span>

          <span className="shrink-0 space-x-1 text-right">
            <span className={clsx(
              'inline-block rounded px-1.5 py-0.5 text-xs font-medium',
              raison.code === 'twin_suspected' ? 'bg-red-100 text-red-800'
                : raison.probablyOutOfScope ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-700',
            )}>
              {raison.label}
            </span>
            {paiement.possiblyTruncated && (
              <span className="inline-block rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">
                communication coupée
              </span>
            )}
            {paiement.unallocatedCents !== paiement.amountCents && (
              <span className="inline-block rounded bg-blue-100 px-1.5 py-0.5 text-xs text-blue-800">
                sur {euros(paiement.amountCents)}
              </span>
            )}
          </span>
        </button>
      </div>

      {ouvert && <Detail paiement={paiement} raison={raison} apresAction={apresAction} />}
    </div>
  );
}

function Detail({
  paiement, raison, apresAction,
}: {
  paiement: PendingPayment;
  raison: ReturnType<typeof queueReason>;
  apresAction: () => void;
}) {
  const [suggestions, setSuggestions] = useState<Suggestion[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [dejaAffecte, setDejaAffecte] = useState<Awaited<ReturnType<typeof loadAllocations>>>([]);
  /*
    Le libellé complet du relevé, lu UNE fois pour les deux panneaux qui s'en
    servent : « Communication complète » l'affiche, « Ce que ce virement dit »
    y cherche les factures citées. La communication bancaire s'arrête à 53
    caractères, et c'est souvent ici que se trouvent les factures qu'aucun
    rapprochement n'a vues.
  */
  const [texte, setTexte] = useState<TextePaiement | null>(null);
  /*
    Si cette lecture échoue, `CommunicationComplete` reprend la main : on lui
    passe `undefined`, il relit et affiche SON erreur. Laisser `texte` à `null`
    le figerait sur un chargement qui n'arrive jamais — un écran qui tourne
    sans fin en dit moins qu'un écran qui dit ce qui a échoué.
  */
  const [texteEnEchec, setTexteEnEchec] = useState(false);
  const horsPerimetre = classifyOutOfScope(paiement.communicationRaw);

  const recharger = useCallback(async () => {
    try {
      setDejaAffecte(await loadAllocations(paiement.id));
    } catch (e) {
      toast.error(messageErreur(e, 'Lecture des affectations impossible'));
    }
  }, [paiement.id]);

  useEffect(() => {
    let annule = false;
    (async () => {
      // Les lectures sont indépendantes et se règlent séparément : une piste
      // en échec ne doit plus faire disparaître tout le reste de l'écran.
      const [s, a, t] = await Promise.allSettled([
        suggestFamilies(
          {
            amountCents: paiement.unallocatedCents,
            communication: paiement.communicationRaw,
            counterpartyIban: paiement.counterpartyIban,
            counterpartyName: paiement.counterpartyName,
          },
          suggestGatewaySupabase,
        ),
        loadAllocations(paiement.id),
        loadPaymentText(paiement.id),
      ]);
      if (annule) return;
      if (s.status === 'fulfilled') setSuggestions(s.value);
      else { setSuggestions([]); setErreur(messageErreur(s.reason, 'Recherche des familles impossible')); }
      if (a.status === 'fulfilled') setDejaAffecte(a.value);
      if (t.status === 'fulfilled') setTexte(t.value);
      else setTexteEnEchec(true);
    })();
    return () => { annule = true; };
  }, [paiement]);

  const agir = async (action: () => Promise<void>, message: string) => {
    setEnCours(true);
    try {
      await action();
      toast.success(message);
      apresAction();
    } catch (e) {
      // Les invariants de la base remontent ici : une répartition qui dépasse
      // le montant du paiement ou de la facture est refusée par PostgreSQL et
      // l'erreur est montrée telle quelle, jamais avalée.
      toast.error(messageErreur(e, 'Écriture refusée'));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div className="space-y-4 border-t border-gray-200 px-4 py-4 pl-11">
      <p className="text-sm text-gray-700">{raison.hint}</p>

      {paiement.jumeau && (
        <VirementLuDeuxFois sens="entree" ligne={paiement} jumeau={paiement.jumeau} enCours={enCours} executer={agir} />
      )}

      {/*
        La LECTURE d'abord, la matière brute ensuite. « Ce que ce virement
        dit » répond à la question posée — où va cet argent, et pourquoi les
        comptes ne tombent pas juste ; la communication et le libellé du relevé
        servent à la vérifier, pas à la deviner.
      */}
      <FacturesCitees
        paiement={paiement}
        libelleDuReleve={texte?.freeText ?? null}
        etatDuReleve={texteEnEchec ? 'echec' : texte ? 'lu' : 'lecture'}
        enCours={enCours}
        agir={agir}
      />

      <CommunicationComplete
        paymentId={paiement.id}
        communicationConnue={paiement.communicationRaw}
        texte={texteEnEchec ? undefined : texte}
      />

      {dejaAffecte.length > 0 && (
        <div className="rounded-md border border-blue-200 bg-blue-50 p-3">
          <p className="text-xs font-medium uppercase tracking-wide text-blue-800">Déjà affecté</p>
          <ul className="mt-2 space-y-1 text-sm text-blue-900">
            {dejaAffecte.map((a) => (
              <li key={a.id} className="flex flex-wrap items-center gap-2">
                <span className="tabular-nums">{euros(a.amountCents)}</span>
                {a.invoiceNumber
                  ? <LienFacture invoiceNumber={a.invoiceNumber} pdfUrl={a.invoicePdfUrl} />
                  : <span>{a.target}</span>}
                {a.notes && <span className="text-xs text-blue-700">— {a.notes}</span>}
                <button
                  disabled={enCours}
                  onClick={async () => {
                    setEnCours(true);
                    try {
                      await undoAllocation(a.id);
                      toast.success('Affectation défaite : le montant retourne dans la file');
                      await recharger();
                    } catch (e) {
                      toast.error(messageErreur(e, 'Annulation refusée'));
                    } finally {
                      setEnCours(false);
                    }
                  }}
                  className="inline-flex items-center gap-1 rounded border border-blue-300 bg-white px-1.5 py-0.5 text-xs font-medium text-blue-800 hover:bg-blue-100 disabled:opacity-50"
                >
                  <Undo2 className="h-3 w-3" /> Défaire
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <button
          disabled={enCours}
          onClick={() => agir(
            () => markOutOfScope(
              paiement.id,
              paiement.unallocatedCents,
              horsPerimetre ? horsPerimetre.label : raison.label,
            ),
            'Paiement classé hors périmètre',
          )}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <CircleSlash className="h-4 w-4" />
          Hors périmètre
          {horsPerimetre && <span className="text-xs text-gray-500">({horsPerimetre.label})</span>}
        </button>
      </div>

      {erreur && (
        <p className="flex items-start gap-1.5 rounded-md border border-red-200 bg-red-50 p-2 text-sm text-red-800">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{erreur}</span>
        </p>
      )}

      {suggestions === null && (
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Loader2 className="h-4 w-4 animate-spin" /> Recherche des familles…
        </div>
      )}

      {suggestions && suggestions.length > 0 && (
        <div className="space-y-3">
          <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-gray-500">
            <Sparkles className="h-3.5 w-3.5" /> Familles suggérées
          </p>
          {suggestions.map((s) => (
            <CarteSuggestion
              key={s.userId}
              suggestion={s}
              paiement={paiement}
              enCours={enCours}
              agir={agir}
            />
          ))}
        </div>
      )}

      <RechercheManuelle
        paiement={paiement}
        // Lancée d'office quand aucune piste automatique n'a répondu : c'est le
        // cas où l'écran n'offrait rien, et où l'administration devait deviner
        // qu'elle pouvait chercher.
        auto={suggestions !== null && suggestions.length === 0}
        enCours={enCours}
        agir={agir}
      />
    </div>
  );
}

/**
 * Chercher une facture par son numéro, ou une famille par son nom.
 *
 * Toujours disponible, y compris quand les suggestions ont répondu : une
 * suggestion peut être plausible et fausse, et il faut pouvoir passer outre.
 */
function RechercheManuelle({
  paiement, auto, enCours, agir,
}: {
  paiement: PendingPayment;
  auto: boolean;
  enCours: boolean;
  agir: (action: () => Promise<void>, message: string) => Promise<void>;
}) {
  const prefill = (paiement.communicationRaw ?? paiement.counterpartyName ?? '').trim().slice(0, 60);
  const [texte, setTexte] = useState(prefill);
  const [resultats, setResultats] = useState<Suggestion[] | null>(null);
  const [recherche, setRecherche] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [autoFait, setAutoFait] = useState(false);

  const lancer = useCallback(async (requete: string) => {
    if (requete.trim().length < 3) {
      setErreur('Trois caractères au minimum.');
      return;
    }
    setRecherche(true);
    setErreur(null);
    try {
      setResultats(await searchManually(requete, paiement.unallocatedCents));
    } catch (e) {
      setErreur(messageErreur(e, 'Recherche impossible'));
    } finally {
      setRecherche(false);
    }
  }, [paiement.unallocatedCents]);

  useEffect(() => {
    if (!auto || autoFait || prefill.length < 3) return;
    setAutoFait(true);
    void lancer(prefill);
  }, [auto, autoFait, prefill, lancer]);

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3">
      <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-gray-500">
        <Search className="h-3.5 w-3.5" /> Chercher à la main
      </p>
      <p className="mt-1 text-xs text-gray-500">
        Un numéro de facture, même partiel — <span className="font-mono">2601466</span>,
        {' '}<span className="font-mono">NDSC-2601466</span> — ou un nom de parent ou d'enfant.
      </p>

      <form
        className="mt-2 flex flex-wrap gap-2"
        onSubmit={(e) => { e.preventDefault(); void lancer(texte); }}
      >
        <input
          value={texte}
          onChange={(e) => setTexte(e.target.value)}
          placeholder="Numéro de facture ou nom"
          className="min-w-0 flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm"
        />
        <button
          type="submit"
          disabled={recherche}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          {recherche ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          Chercher
        </button>
      </form>

      {erreur && <p className="mt-2 text-sm text-red-700">{erreur}</p>}

      {resultats?.length === 0 && !recherche && (
        <p className="mt-3 text-sm text-gray-600">
          Rien trouvé pour cette recherche. Essayez les sept chiffres du numéro de facture
          seuls, ou le nom de famille seul.
        </p>
      )}

      {resultats && resultats.length > 0 && (
        <div className="mt-3 space-y-3">
          {resultats.map((s) => (
            <CarteSuggestion
              key={s.userId}
              suggestion={s}
              paiement={paiement}
              enCours={enCours}
              agir={agir}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function CarteSuggestion({
  suggestion, paiement, enCours, agir,
}: {
  suggestion: Suggestion;
  paiement: PendingPayment;
  enCours: boolean;
  agir: (action: () => Promise<void>, message: string) => Promise<void>;
}) {
  // La répartition proposée est un point de départ modifiable, pas un verdict.
  const [parts, setParts] = useState<Record<string, number>>(() => {
    const initial: Record<string, number> = {};
    for (const p of suggestion.proposedSplit) initial[p.invoice.invoiceId] = p.amountCents;
    if (suggestion.exactInvoice) initial[suggestion.exactInvoice.invoiceId] = paiement.unallocatedCents;
    return initial;
  });

  /*
    La destination du reliquat. « provision » par defaut, et c'est un choix :
    dans les mots de l'administration, « elle devrait avoir 25 euros de
    provisions ». Un reliquat qui reste dans la file est le cas rare ; en faire
    le defaut le rendrait invisible, et c'est exactement ce qui a fait
    disparaitre 25,00 EUR le 4 septembre.

    Les trois destinations sont NOMMEES, « laisse dans la file » comprise : le
    libelle du bouton dit ce qui sera ecrit, y compris ce qui ne le sera pas.
  */
  const [destination, setDestination] = useState<DestinationDuReste>('provision');

  /*
    Le compte complet de la famille, à la demande. Une suggestion se juge sur
    ce que la famille doit DÉJÀ : « aucune facture ouverte » peut vouloir dire
    « ce n'est pas elle » comme « elle a tout payé et verse d'avance », et rien
    ne distinguait les deux sans ouvrir un autre écran.
  */
  const [compteOuvert, setCompteOuvert] = useState(false);

  const demande = {
    unallocatedCents: paiement.unallocatedCents,
    parts,
    provisionUserId: suggestion.userId,
    destinationDuReste: destination,
  };
  const repartition = construireRepartition(demande);
  const reste = repartition.reste;
  const sur = new Set(suggestion.sources);

  return (
    <div className={clsx(
      'rounded-lg border bg-white p-3',
      isConfident(suggestion) ? 'border-green-300 ring-1 ring-green-100' : 'border-gray-200',
    )}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="flex items-center gap-2 font-medium text-gray-900">
            {suggestion.label}
            {isConfident(suggestion) && (
              <span className="inline-flex items-center gap-1 rounded bg-green-100 px-1.5 py-0.5 text-xs font-medium text-green-800">
                <BadgeCheck className="h-3 w-3" /> forte
              </span>
            )}
          </p>
          <ul className="mt-1 space-y-0.5 text-xs text-gray-600">
            {suggestion.reasons.map((r) => <li key={r}>· {r}</li>)}
          </ul>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setCompteOuvert(!compteOuvert)}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
          >
            {compteOuvert ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            Voir le compte
          </button>

          {paiement.counterpartyIban && !sur.has('iban_confirmed') && (
            <button
              disabled={enCours}
              onClick={() => agir(
                () => linkIbanToFamily(paiement.counterpartyIban!, suggestion.userId, suggestion.label),
                'Compte rattaché : le prochain paiement sera proposé en tête',
              )}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <Link2 className="h-3.5 w-3.5" /> Rattacher ce compte
            </button>
          )}
        </div>
      </div>

      {compteOuvert && (
        <div className="mt-3">
          {/* La clé porte le reliquat : une écriture le change, le panneau relit. */}
          <CompteDeLaFamille
            key={paiement.unallocatedCents}
            userId={suggestion.userId}
            paiementExamine={paiement.id}
            ibanExamine={paiement.counterpartyIban}
          />
        </div>
      )}

      {suggestion.openInvoices.length === 0 ? (
        <p className="mt-3 rounded bg-amber-50 px-2 py-1.5 text-xs text-amber-800">
          Aucune facture ouverte pour cette famille. Le solde peut aller en provision.
        </p>
      ) : (
        <table className="mt-3 w-full text-sm">
          <thead className="text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="py-1 text-left font-medium">Facture</th>
              <th className="py-1 text-left font-medium">Échéance</th>
              <th className="py-1 text-right font-medium">Reste dû</th>
              <th className="py-1 text-right font-medium">À affecter</th>
            </tr>
          </thead>
          <tbody>
            {suggestion.openInvoices.map((f) => (
              <tr key={f.invoiceId} className="border-t border-gray-100">
                <td className="py-1.5">
                  <LienFacture invoiceNumber={f.invoiceNumber} pdfUrl={f.pdfUrl} />
                </td>
                <td className="py-1.5 text-xs text-gray-500">{dateCourte(f.dueDate)}</td>
                <td className="py-1.5 text-right tabular-nums text-gray-700">{euros(f.outstandingCents)}</td>
                <td className="py-1.5 text-right">
                  {/*
                    Plus de `input[type=number]` reformaté à chaque frappe :
                    taper « 30 » y donnait « 3,00 » puis « 3,01 », le curseur
                    repartant à la fin après chaque réécriture. Et la molette y
                    changeait un montant d'un centime sans qu'on ait rien tapé.
                  */}
                  <SaisieMontant
                    valeurCents={parts[f.invoiceId] ?? 0}
                    onChange={(centimes) => setParts({ ...parts, [f.invoiceId]: centimes })}
                    ariaLabel={`Montant à affecter à la facture ${f.invoiceNumber}`}
                    className="w-24"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/*
        Le sort du reliquat, choisi AVANT d'ecrire et dans la meme ecriture.

        L'ecran avait deux boutons — « Affecter » et « Reste en provision » —
        qui s'excluaient : le premier perdait le reliquat, le second jetait les
        montants saisis dans les cases. Ils composent desormais.
      */}
      {reste > 0 && (
        <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3">
          <p className="text-xs font-medium text-amber-900">
            Il reste {euros(reste)}. Où va ce reliquat ?
          </p>
          <div className="mt-2 space-y-1.5">
            {([
              ['provision', `En provision pour ${suggestion.label}`,
               'Cette famille paiera une facture future avec ce montant.'],
              ['hors_perimetre', 'Hors périmètre',
               'Garderie, facturation partenaire : rien à voir avec les centres de vacances.'],
              ['file', 'Laissé dans la file',
               'À traiter plus tard. Le paiement restera à rapprocher.'],
            ] as [DestinationDuReste, string, string][]).map(([valeur, libelle, aide]) => (
              <label key={valeur} className="flex items-start gap-2 text-sm">
                <input
                  type="radio"
                  name={`reste-${paiement.id}-${suggestion.userId}`}
                  checked={destination === valeur}
                  onChange={() => setDestination(valeur)}
                  className="mt-1 h-3.5 w-3.5 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span>
                  <span className="font-medium text-amber-900">{libelle}</span>
                  <span className="block text-xs text-amber-700">{aide}</span>
                </span>
              </label>
            ))}
          </div>
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 pt-3">
        <p className={clsx(
          'text-xs tabular-nums',
          repartition.probleme ? 'text-red-700'
            : placeEntierement(repartition, demande) ? 'text-gray-600' : 'text-amber-700',
        )}>
          {phraseDeRepartition(repartition, demande)}
        </p>
        <button
          disabled={enCours || repartition.probleme !== null}
          onClick={() => agir(
            () => allocatePayment(paiement.id, repartition.lignes),
            phraseDeRepartition(repartition, demande),
          )}
          className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          {reste > 0 && destination === 'provision'
            ? <Wallet className="h-4 w-4" />
            : <Landmark className="h-4 w-4" />}
          Écrire cette répartition
        </button>
      </div>
    </div>
  );
}
